import {
  AiResultContext,
  AiRunnerConfig,
  AiRunnerVariant,
} from 'src/app/features/ai/components/ai-runner/ai-runner.model';
import { AiRun, AiTicketPipeline } from 'src/app/features/ai/core/models/ai.model';
import {
  TicketAiActionDeps,
  ticketAttachmentAction,
  ticketCommentAction,
  ticketNumber,
  ticketTagAction,
} from './ticket-ai-actions';

/**
 * Análise de chamado por IA (chamado 0000579137): tudo que é específico de chamado para o
 * `ai-runner-dialog` genérico — pipelines, contexto, apresentação e gravação do resultado.
 *
 * Cada tipo de análise é uma pipeline própria no redline, com agentes especialistas e uma skill por
 * etapa: as instruções ficam lá, não aqui. Todas recebem `input.data` e
 * `input.options.instrucaoExtra`.
 *
 * `input.data` (o chamado) é montado no SERVIDOR (TicketAiInputBuilder, redline) a partir de
 * `refType`/`refId` e das escolhas em `input.options` (incluir*): lá o conteúdo é mascarado
 * (CPF/CNPJ, e-mail, telefone, senhas e tokens), limitado em tamanho e completado com chamados
 * parecidos e o conteúdo dos anexos de texto. O navegador não manda o chamado.
 */

/**
 * As pipelines do modal NÃO são mais uma lista fixa de UUIDs: o redline devolve as pipelines do tipo
 * `ticket-pipeline` que o usuário pode executar neste chamado (GET tickets/{id}/ai-pipelines), já
 * com rótulo e ordem do gatilho. Cada variante abaixo casa pelo slug e só define como
 * o resultado aparece e o que dá para gravar; pipeline sem variante usa a apresentação padrão.
 */
export type TicketAnalysisDeps = TicketAiActionDeps;

function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
}

function scoreSeverity(value: unknown): 'success' | 'warn' | 'danger' {
  const n = Number(value);
  return n >= 7 ? 'success' : n >= 4 ? 'warn' : 'danger';
}

function readScore(ctx: AiResultContext): number | null {
  const n = Number((ctx.json as { nota?: unknown } | null)?.nota);
  return Number.isFinite(n) ? Math.max(0, Math.min(10, Math.round(n))) : null;
}

/** Comentário do score: só pontos fortes, fracos e o que falta (sem o JSON bruto). */
function scoreComment(ctx: AiResultContext): string | null {
  const json = ctx.json as Record<string, unknown> | null;
  const score = readScore(ctx);
  if (!json || score === null) return null;
  const section = (title: string, items: string[]) =>
    items.length ? `\n\n**${title}**\n${items.map(item => `- ${item}`).join('\n')}` : '';
  return (
    `**Nota técnica: ${score}/10**` +
    (json['resumo'] ? `\n\n${json['resumo']}` : '') +
    section('Pontos fortes', asList(json['pontos_fortes'])) +
    section('Pontos fracos', asList(json['pontos_fracos'])) +
    section('O que falta para executar', asList(json['informacoes_faltantes'])) +
    section('Perguntas sugeridas', asList(json['perguntas']))
  );
}

function variants(deps: TicketAnalysisDeps): AiRunnerVariant[] {
  const comment = ticketCommentAction(deps);
  const publicComment = ticketCommentAction(deps, { key: 'ticket-comment-pub', pub: true });
  const attachMd = ticketAttachmentAction(deps, { format: 'md' });
  const attachPdf = ticketAttachmentAction(deps, { format: 'pdf' });

  return [
    {
      key: 'score',
      pipeline: 'chamado-nota-tecnica',
      icon: 'pi pi-star',
      resultFormat: 'json',
      resultView: {
        fields: [
          { path: 'nota', label: 'Nota', display: 'meter', max: 10, severity: scoreSeverity },
          { path: 'resumo', label: 'Resumo' },
          {
            path: 'criterios',
            label: 'Critérios',
            display: 'list',
            format: value =>
              Object.entries((value as Record<string, unknown>) ?? {})
                .map(([k, v]) => `${k.replace('_', ' ')}: ${v}/10`)
                .join(' · '),
          },
          {
            path: 'pontos_fortes',
            label: 'Pontos fortes',
            display: 'list',
            icon: 'pi pi-thumbs-up',
          },
          {
            path: 'pontos_fracos',
            label: 'Pontos fracos',
            display: 'list',
            icon: 'pi pi-thumbs-down',
          },
          {
            path: 'informacoes_faltantes',
            label: 'O que falta',
            display: 'list',
            icon: 'pi pi-question-circle',
          },
          {
            path: 'perguntas',
            label: 'Perguntas ao solicitante',
            display: 'list',
            icon: 'pi pi-comments',
          },
        ],
      },
      resultActions: [
        ticketTagAction(deps, {
          label: 'Adicionar tag de nota no chamado',
          tag: ctx => {
            const score = readScore(ctx);
            return score === null ? null : `score ${score}/10`;
          },
          replacePrefix: 'score ',
        }),
        ticketCommentAction(deps, {
          label: 'Comentário privado com pontos fortes e fracos',
          text: scoreComment,
        }),
      ],
      actionPolicy: { mode: 'choose', preselect: ['ticket-tag'] },
    },
    {
      key: 'debug',
      pipeline: 'chamado-debug',
      icon: 'pi pi-wrench',
      resultFormat: 'markdown',
      resultActions: [comment, attachMd, attachPdf],
    },
    {
      key: 'missing',
      pipeline: 'chamado-informacoes-faltantes',
      icon: 'pi pi-question-circle',
      resultFormat: 'markdown',
      resultActions: [comment, publicComment],
    },
    {
      key: 'plan',
      pipeline: 'chamado-plano-de-implementacao',
      icon: 'pi pi-sitemap',
      resultFormat: 'markdown',
      resultActions: [attachMd, attachPdf, comment],
    },
    {
      key: 'acceptance',
      pipeline: 'chamado-criterios-de-aceite',
      icon: 'pi pi-check-square',
      resultFormat: 'markdown',
      resultActions: [comment, attachMd],
    },
    {
      key: 'product',
      pipeline: 'chamado-visao-de-produto',
      icon: 'pi pi-chart-line',
      resultFormat: 'markdown',
      resultActions: [attachMd, attachPdf, comment],
    },
    {
      key: 'summary',
      pipeline: 'chamado-resumo-executivo',
      icon: 'pi pi-align-left',
      resultFormat: 'markdown',
      resultActions: [comment],
    },
    {
      key: 'triage',
      pipeline: 'chamado-triagem',
      icon: 'pi pi-filter',
      resultFormat: 'json',
      resultView: {
        fields: [
          { path: 'tipo', label: 'Tipo', display: 'badge', severity: () => 'info' },
          {
            path: 'urgencia',
            label: 'Urgência',
            display: 'badge',
            severity: v =>
              v === 'critica' || v === 'alta' ? 'danger' : v === 'media' ? 'warn' : 'success',
          },
          { path: 'complexidade', label: 'Complexidade', display: 'badge', severity: () => 'info' },
          { path: 'modulo_provavel', label: 'Módulo provável' },
          {
            path: 'afeta_outros_clientes',
            label: 'Pode afetar outros clientes',
            display: 'badge',
            format: v => (v ? 'Sim' : 'Não'),
            severity: v => (v ? 'warn' : 'success'),
          },
          { path: 'responsavel_sugerido', label: 'Perfil sugerido' },
          { path: 'justificativa', label: 'Justificativa', display: 'list' },
        ],
      },
      resultActions: [
        ticketCommentAction(deps, {
          label: 'Salvar triagem como comentário privado',
          text: ctx => {
            const j = ctx.json as Record<string, unknown> | null;
            if (!j) return null;
            return [
              `- **Tipo:** ${j['tipo'] ?? '—'}`,
              `- **Urgência:** ${j['urgencia'] ?? '—'}`,
              `- **Complexidade:** ${j['complexidade'] ?? '—'}`,
              `- **Módulo provável:** ${j['modulo_provavel'] ?? '—'}`,
              `- **Pode afetar outros clientes:** ${j['afeta_outros_clientes'] ? 'sim' : 'não'}`,
              `- **Perfil sugerido:** ${j['responsavel_sugerido'] ?? '—'}`,
              ...asList(j['justificativa']).map(item => `  - ${item}`),
            ].join('\n');
          },
        }),
      ],
    },
  ];
}

/**
 * O que o componente sabe e o modal não: pipelines liberadas ao usuário no chamado, se ele pode
 * executar e como retomar uma execução ao abrir.
 */
export interface TicketAnalysisSession {
  pipelines: AiTicketPipeline[];
  /** Pode iniciar execuções (tem alguma pipeline executável e a permissão do módulo). */
  canRun: boolean;
  resume: () => Promise<AiRun | null>;
  /** Todas as execuções do chamado que o usuário pode ver. */
  history: () => Promise<AiRun[]>;
  /** O usuário já viu/decidiu sobre o resultado da execução: não reabre sozinho (fica no histórico). */
  markDecided: (runId: string) => void;
}

/** Variantes de apresentação: as conhecidas (por slug) com o rótulo do gatilho + padrão para as novas. */
function presentation(deps: TicketAnalysisDeps, available: AiTicketPipeline[]): AiRunnerVariant[] {
  const known = variants(deps);
  const fallbackActions = [
    ticketCommentAction(deps),
    ticketAttachmentAction(deps, { format: 'md' }),
  ];
  const merged = known.map(variant => {
    const meta = available.find(p => p.slug === variant.pipeline);
    return meta ? { ...variant, label: meta.label } : variant;
  });
  for (const meta of available) {
    if (known.some(v => v.pipeline === meta.slug)) continue;
    merged.push({
      key: meta.slug,
      pipeline: meta.slug,
      label: meta.label,
      icon: 'pi pi-sparkles',
      resultFormat: 'markdown',
      resultActions: fallbackActions,
    });
  }
  return merged;
}

/** Config do `ai-runner-dialog` para o chamado atual. */
export function buildTicketAnalysisConfig(
  deps: TicketAnalysisDeps,
  session: TicketAnalysisSession
): AiRunnerConfig {
  const ticket = deps.ticket();
  const all = presentation(deps, session.pipelines);
  return {
    title: 'Análise por IA',
    description: ticket && ticketNumber(ticket) ? `Chamado #${ticketNumber(ticket)}` : undefined,
    help: 'Escolha o tipo de análise e o que enviar. A IA lê o chamado e, no fim, você decide o que gravar.',
    icon: 'pi pi-sparkles',
    width: '56rem',
    mode: 'preset',
    target: {
      kind: 'pipeline',
      pipelines: session.pipelines.map(p => ({ id: p.id })),
      missingMessage:
        'Algumas análises não estão disponíveis neste grupo (pipelines do tipo "ticket-pipeline" habilitadas).',
    },
    // Sem pipelines para escolher (só leitura), as variantes não viram cartões: só apresentam o
    // resultado retomado (`resultVariants`).
    variants: session.pipelines.length ? all : [],
    resultVariants: all,
    access: {
      canRun: () => session.canRun,
      canApply: () => session.canRun,
      denyReason: () => 'Somente consulta: o usuário logado só possui permissão de leitura.',
      onDenied: 'readonly',
    },
    resume: () => session.resume(),
    history: () => session.history(),
    historyLabel: 'Resultados deste chamado',
    labels: {
      historyEmpty: 'Nenhum resultado disponível neste chamado.',
    },
    hooks: {
      onDiscard: run => session.markDecided(run.id),
      // Terminou com o modal aberto: o usuário viu, o resultado fica só no histórico.
      onResult: run => session.markDecided(run.id),
    },
    variantsLabel: 'Tipo de análise',
    // A escolha do que enviar fica no navegador e volta marcada na próxima análise.
    storageKey: 'v3ndor-ticket-analysis',
    optionsLabel: 'O que enviar para a IA',
    options: [
      {
        key: 'incluirComentariosPrivados',
        type: 'checkbox',
        label: 'Comentários privados',
        icon: 'pi pi-lock',
        help: 'Sem marcar, só os comentários públicos vão para a IA.',
        default: true,
        remember: true,
        send: true,
      },
      {
        key: 'incluirAnexos',
        type: 'checkbox',
        label: 'Anexos',
        icon: 'pi pi-paperclip',
        help: 'Vai a lista de anexos e, com o aceite abaixo, o conteúdo: PDF, Word/Excel/PowerPoint, imagens e textos são lidos por completo (até 15 páginas por análise).',
        default: true,
        remember: true,
        send: true,
      },
      {
        // Aceite exigido a cada análise com anexos (não fica lembrado): o conteúdo de anexos não é
        // 100% mascarável. Sem ele, vão só os metadados (redline, TicketAiInputBuilder).
        key: 'concordoAnexos',
        parent: 'incluirAnexos',
        type: 'checkbox',
        label: 'Estou ciente: o conteúdo dos anexos não é 100% mascarável',
        icon: 'pi pi-exclamation-triangle',
        help: 'Anexos podem conter dados de clientes (nomes, CPF, prints de tela) que não dá para mascarar por completo. Para provedores que aceitam dados de cliente, o arquivo vai como está; para os demais, vai só o texto extraído, mascarado. Desmarque "Anexos" para analisar sem eles.',
        required: true,
        default: false,
        remember: false,
        visible: values => values['incluirAnexos'] !== false,
        send: true,
      },
      {
        key: 'incluirDocumentacoes',
        type: 'checkbox',
        label: 'Documentações',
        icon: 'pi pi-book',
        help: 'Título, descrição, categoria e versão das documentações ligadas ao chamado.',
        default: true,
        remember: true,
        send: true,
      },
      {
        key: 'incluirSimilares',
        type: 'checkbox',
        label: 'Chamados parecidos',
        icon: 'pi pi-search',
        help: 'Busca chamados parecidos e documentações relacionadas na base do Jarvis. Vão só número, título e um trecho, sem dados de clientes.',
        default: true,
        remember: true,
        send: true,
      },
      {
        key: 'instrucaoExtra',
        type: 'textarea',
        label: 'Algo mais a considerar? (opcional)',
        placeholder: 'Ex.: foque na rotina de faturamento; o cliente usa a versão 8.',
        rows: 2,
        // Vai em `input.options.instrucaoExtra`: as etapas das pipelines usam como foco.
        send: true,
      },
    ],
    live: true,
    showSteps: false,
    refType: 'ticket',
    refId: ticket?.id,
    submitLabel: 'Analisar',
    actionsLabel: 'O que fazer com o resultado',
    applyActionsLabel: 'Gravar no chamado',
    // "Recusar" volta para a revisão com o motivo no foco da próxima análise.
    rejectFeedbackOption: 'instrucaoExtra',
  };
}
